/**
 * @voltxampere/ui — Time base standard scope values (Sprint 15)
 *
 * Industry-standard 1-2-5 sequence from 1 ns/div to 1 s/div.
 * Mouse wheel on the waveform panel snaps the time base to the
 * next / previous standard value rather than scaling continuously
 * — this matches every benchtop scope (Rigol, Tektronix, Keysight)
 * and keeps the on-screen division spacing consistent so users can
 * eyeball amplitudes against the major gridlines.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */
/** Standard seconds-per-division values, ascending. */
export const STANDARD_TIME_BASE_VALUES = [
    1e-9, 2e-9, 5e-9, 1e-8, 2e-8, 5e-8, 1e-7, 2e-7, 5e-7, 1e-6, 2e-6, 5e-6, 1e-5, 2e-5, 5e-5, 1e-4,
    2e-4, 5e-4, 1e-3, 2e-3, 5e-3, 1e-2, 2e-2, 5e-2, 1e-1, 2e-1, 5e-1, 1,
];
export const MIN_TIME_BASE_S_PER_DIV = STANDARD_TIME_BASE_VALUES[0] ?? 1e-9;
export const MAX_TIME_BASE_S_PER_DIV = STANDARD_TIME_BASE_VALUES[STANDARD_TIME_BASE_VALUES.length - 1] ?? 1;
/**
 * Snap an arbitrary value (e.g. an autoFit suggestion) to the
 * closest standard value. Ties prefer the smaller (faster sweep)
 * value so visible detail wins over wider window.
 */
export function snapTimeBase(seconds) {
    if (!Number.isFinite(seconds) || seconds <= 0) {
        return MIN_TIME_BASE_S_PER_DIV;
    }
    if (seconds <= MIN_TIME_BASE_S_PER_DIV)
        return MIN_TIME_BASE_S_PER_DIV;
    if (seconds >= MAX_TIME_BASE_S_PER_DIV)
        return MAX_TIME_BASE_S_PER_DIV;
    let best = STANDARD_TIME_BASE_VALUES[0] ?? MIN_TIME_BASE_S_PER_DIV;
    let bestDelta = Math.abs(Math.log10(seconds) - Math.log10(best));
    for (const v of STANDARD_TIME_BASE_VALUES) {
        const delta = Math.abs(Math.log10(seconds) - Math.log10(v));
        if (delta < bestDelta) {
            best = v;
            bestDelta = delta;
        }
    }
    return best;
}
/**
 * Step `current` to the next standard value. `direction` follows
 * the scope-knob convention: `"in"` zooms in (smaller s/div, more
 * resolution), `"out"` zooms out (larger window).
 */
export function stepTimeBase(current, direction) {
    // Snap first so an off-grid input still moves predictably.
    const snapped = snapTimeBase(current);
    const idx = STANDARD_TIME_BASE_VALUES.findIndex((v) => v === snapped);
    if (idx === -1)
        return snapped;
    const target = direction === "in" ? idx - 1 : idx + 1;
    if (target < 0)
        return STANDARD_TIME_BASE_VALUES[0] ?? snapped;
    if (target >= STANDARD_TIME_BASE_VALUES.length) {
        return STANDARD_TIME_BASE_VALUES[STANDARD_TIME_BASE_VALUES.length - 1] ?? snapped;
    }
    return STANDARD_TIME_BASE_VALUES[target] ?? snapped;
}
//# sourceMappingURL=time-base.js.map